import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ContactForm from "../components/ContactForm";
import SEO from "../components/SEO";
import { servicesData, processSteps, whyChooseUsData, faqData, pricingPlans } from "../data/mock";
import { ArrowRight, CheckCircle, ArrowLeft } from "lucide-react";
import { Toaster } from "../components/ui/sonner";

const ServicePage = () => {
  const { serviceId } = useParams();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const service = servicesData.find((s) => s.id === serviceId);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Service Not Found</h1>
          <Link to="/" className="text-[#1e3a8a] hover:text-[#d4af37] font-semibold">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEO 
        title={`${service.title} - Trademark Brand Logo`}
        description={service.description}
        keywords={service.keywords.join(", ")}
        ogTitle={`Professional ${service.title} Services`}
        ogDescription={service.description}
      />
      <Header onOpenForm={() => setIsFormOpen(true)} />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="section-padding bg-gradient-to-br from-[#f8fafc] via-white to-[#e0f2fe]">
          <div className="container-custom px-4">
            {/* Breadcrumb */}
            <div className="mb-8">
              <Link
                to="/"
                className="inline-flex items-center text-[#1e3a8a] hover:text-[#d4af37] transition-colors duration-300 font-medium"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  {service.title}
                </h1>
                <p className="text-xl text-gray-700 leading-relaxed mb-8">
                  {service.description}
                </p>
                <p className="text-base text-gray-600 leading-relaxed mb-8">
                  Our comprehensive {service.title.toLowerCase()} provide complete legal protection for your 
                  business. We handle every aspect of the process, from initial consultation and filing to 
                  monitoring and enforcement, ensuring your intellectual property receives maximum protection 
                  under federal law.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => setIsFormOpen(true)}
                    className="btn-primary flex items-center justify-center space-x-2"
                  >
                    <span>Get Started</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  <Link to="/#contact" className="btn-secondary flex items-center justify-center space-x-2">
                    <span>Contact Us</span>
                  </Link>
                </div>
              </div>

              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a8a]/20 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="section-padding bg-white">
          <div className="container-custom px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                What's Included in Our {service.title}
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {service.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-4 bg-gradient-to-br from-white to-gray-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100"
                  >
                    <CheckCircle className="w-6 h-6 text-[#d4af37] mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">{feature}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Professional service ensuring complete compliance and maximum protection
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="section-padding bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] text-white">
          <div className="container-custom px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Our Simple 3-Step Process
              </h2>
              <p className="text-lg text-white/90 max-w-2xl mx-auto leading-relaxed">
                We've streamlined the {service.title.toLowerCase()} process to make it as easy and 
                stress-free as possible for you.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {processSteps.map((step, index) => (
                <div key={index} className="relative">
                  {index < processSteps.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-1 bg-[#d4af37]/30">
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-[#d4af37] rotate-45"></div>
                    </div>
                  )}

                  <div className="relative bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 h-full">
                    <div className="w-14 h-14 bg-gradient-secondary rounded-full flex items-center justify-center mb-4 shadow-xl">
                      <span className="text-2xl font-bold text-[#1e3a8a]">{step.step}</span>
                    </div>
                    <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                    <p className="text-white/80 leading-relaxed text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="section-padding bg-white">
          <div className="container-custom px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              Why Choose Our {service.title}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {whyChooseUsData.reasons.slice(0, 6).map((reason, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{reason.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm">{reason.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing - Only for Trademark Registration */}
        {serviceId === "trademark-registration" && (
          <section className="section-padding bg-gradient-to-br from-gray-50 to-white">
            <div className="container-custom px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
                Choose Your Package
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
                {pricingPlans.map((plan, index) => (
                  <div
                    key={index}
                    className={`relative bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden ${
                      plan.recommended ? "ring-4 ring-[#d4af37]" : "border border-gray-200"
                    }`}
                  >
                    {plan.recommended && (
                      <div className="absolute top-0 right-0 bg-gradient-secondary text-[#1e3a8a] px-3 py-1 text-xs font-bold rounded-bl-lg">
                        RECOMMENDED
                      </div>
                    )}

                    <div className={`p-5 ${plan.recommended ? "bg-gradient-primary" : "bg-gray-50"}`}>
                      <h3 className={`text-xl font-bold mb-2 ${plan.recommended ? "text-white" : "text-gray-900"}`}>
                        {plan.name}
                      </h3>
                      <div className={`text-3xl font-bold mb-1 ${plan.recommended ? "text-white" : "text-[#1e3a8a]"}`}>
                        {plan.price}
                      </div>
                      <div className={`text-sm ${plan.recommended ? "text-white/90" : "text-gray-600"}`}>
                        {plan.fee}
                      </div>
                    </div>

                    <div className="p-5">
                      <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                        {plan.features.map((feature, idx) => (
                          <div key={idx} className="flex items-start space-x-2">
                            <CheckCircle className="w-4 h-4 text-[#d4af37] mt-0.5 flex-shrink-0" />
                            <span className="text-xs text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => setIsFormOpen(true)}
                        className={`w-full mt-5 py-3 rounded-lg font-bold transition-all duration-300 ${
                          plan.recommended
                            ? "bg-[#d4af37] text-white hover:bg-[#f59e0b]"
                            : "bg-[#1e3a8a] text-white hover:bg-[#1e40af]"
                        }`}
                      >
                        Get Started
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        <section className="section-padding bg-white">
          <div className="container-custom px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqData.slice(0, 5).map((faq, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-xl shadow-md border border-gray-100"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding bg-gradient-primary text-white">
          <div className="container-custom px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Get Started with {service.title}?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
              Don't wait to protect your valuable intellectual property. Start your registration today 
              and secure your business's future.
            </p>
            <button
              onClick={() => setIsFormOpen(true)}
              className="bg-[#d4af37] text-[#1e3a8a] px-10 py-4 rounded-lg font-bold text-lg hover:bg-[#f59e0b] transition-all duration-300 transform hover:scale-105 shadow-xl inline-flex items-center space-x-2"
            >
              <span>Start Your Registration</span>
              <ArrowRight className="w-6 h-6" />
            </button>
          </div>
        </section>
      </main>

      <Footer />
      <ContactForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
      <Toaster position="top-right" />
    </div>
  );
};

export default ServicePage;
